import type { VenueView } from '@sorted/types'
import { Check, ExternalLink, MapPin, Scale, Search, Spinner } from './icons.js'
import { Stars } from './Stars.js'
import { VenueMap } from './VenueMap.js'
import { VenuePhoto } from './VenuePhoto.js'
import {
  fairnessLabel,
  mapsUrl,
  priceLabel,
  ratingLabel,
  reviewsUrl,
  searchUrl,
} from '../lib/format.js'
import { t } from '../lib/i18n.js'

/**
 * One option, as a card.
 *
 * Rendered up to three at a time. The `expanded` variant is the single-card
 * layout used for NARROWING, where the choice is deliberately gone and the
 * one remaining suggestion gets the full treatment - photo, map, the lot.
 * The compact variant drops the map: three maps on one screen is a lot of
 * tiles for a decision that is really about name, rating and fairness.
 */

interface Props {
  venue: VenueView
  token: string
  expanded?: boolean | undefined
  busy?: boolean | undefined
  /** Label on the confirm button - differs between SUGGESTING and NARROWING. */
  cta: string
  onPick: () => void
}

export function VenueCard({ venue, token, expanded, busy, cta, onPick }: Props) {
  const rating = ratingLabel(venue)
  const price = priceLabel(venue.priceLevel)

  return (
    <div className="card venue-option">
      <VenuePhoto
        token={token}
        placeId={venue.placeId}
        hasPhoto={venue.hasPhoto}
        name={venue.name}
        compact={!expanded}
      />

      {/* The name is the link - checking a suggestion should cost one tap on
          the obvious thing. */}
      <h2 className="venue-name">
        <a
          className="venue-link"
          href={mapsUrl(venue.placeId, venue.name)}
          target="_blank"
          rel="noreferrer"
        >
          {venue.name}
          <ExternalLink size={14} aria-hidden />
        </a>
      </h2>

      <div className="venue-meta">
        {/* The rating links to where it came from. Someone who looks twice at
            "4.4 (21.086)" wants to know what those people said, and the whole
            §7b bet is that the card answers enough questions that nobody has
            to go looking - this makes the one question it cannot answer one
            tap instead of a search. */}
        {rating && venue.rating !== null && (
          <a
            className="item rating-link"
            href={reviewsUrl(venue.placeId)}
            target="_blank"
            rel="noreferrer"
          >
            <Stars rating={venue.rating} size={13} />
            {rating}
          </a>
        )}
        {price && <span className="item">{price}</span>}
        {/* Said on the card, not once at the top: with three options the one
            that is a few streets out has to be identifiable, or the whole set
            inherits the doubt. */}
        {venue.outsideArea && (
          <span className="item warn-item">
            <MapPin size={14} aria-hidden />
            {t('suggest.outsideArea')}
          </span>
        )}
      </div>

      {/* The fairness row, and ONLY when there is fairness to state.
      
          With a pinned area nobody gave an origin, so nothing was routed and
          there is no comparison to make. It used to say "in the area you
          picked" — written from the creator's side and shown to the
          recipient, who picked nothing. There is no sentence that belongs
          here in that case, so there is no row. */}
      {venue.etaCreatorS !== null && venue.etaRecipientS !== null && (
        <div className="fairness">
          <Scale size={15} aria-hidden />
          <div>
            <div className="fairness-line">
              {fairnessLabel(venue.etaCreatorS, venue.etaRecipientS, false)}
            </div>
            {expanded && <div className="faint">{t('fair.detail')}</div>}
          </div>
        </div>
      )}

      {expanded && (
        <VenueMap lat={venue.location.lat} lng={venue.location.lng} name={venue.name} />
      )}

      <div className="lookup">
        <a href={mapsUrl(venue.placeId, venue.name)} target="_blank" rel="noreferrer">
          <MapPin size={14} aria-hidden />
          {t('suggest.onMaps')}
        </a>
        <a href={searchUrl(venue.name)} target="_blank" rel="noreferrer">
          <Search size={14} aria-hidden />
          {t('suggest.lookUp')}
        </a>
      </div>

      {/* Every card carries its own confirm. With three on screen the choice
          IS the action, so there is no separate "accept the highlighted one"
          step to get wrong. */}
      <button className="btn btn-primary venue-confirm" disabled={busy} onClick={onPick}>
        {busy ? <Spinner /> : <Check size={18} aria-hidden />}
        {cta}
      </button>
    </div>
  )
}
